<template>
  <div class="p-4 space-y-4">
    <!-- List View -->
    <div v-if="!editing" class="space-y-3">
      <div class="flex items-center justify-between">
        <h3 class="text-xs font-semibold text-gray-800">Analyses</h3>
        <button class="text-xs px-3 py-1.5 rounded bg-gray-900 text-white hover:bg-gray-800" @click="createNew">+ New</button>
      </div>

      <LoadingIndicator v-if="loading" message="Loading analyses…" />

      <div v-else class="space-y-1.5">
        <div
          v-for="item in items"
          :key="item.id"
          @click="editItem(item)"
          class="px-3 py-2 border border-gray-200 rounded hover:bg-gray-50 cursor-pointer transition-colors flex items-center justify-between"
        >
          <div class="min-w-0">
            <div class="text-xs font-medium text-gray-800 flex items-center gap-1.5">
              <span class="text-[10px] font-mono text-gray-400">#{{ item.id }}</span>
              {{ item.name }}
              <span
                class="text-[10px] px-1 py-0.5 rounded"
                :class="item.active ? 'text-green-700 bg-green-50' : 'text-gray-400 bg-gray-100'"
              >{{ item.active ? 'active' : 'off' }}</span>
              <span class="text-[10px] px-1 py-0.5 rounded bg-purple-50 text-purple-700 font-mono">{{ item.model || '—' }}</span>
              <span
                v-if="item.execution_mode === 'agentic'"
                class="text-[10px] px-1 py-0.5 rounded bg-orange-50 text-orange-700 font-mono"
                :title="`Max turns: ${item.max_turns}, max rows/query: ${item.max_rows_per_query}`"
              >agentic</span>
            </div>
            <div class="text-[10px] text-gray-400 mt-0.5">
              {{ item.source_count || 0 }} source{{ item.source_count === 1 ? '' : 's' }} ·
              {{ (item.task_links || []).length }} task{{ (item.task_links || []).length === 1 ? '' : 's' }}
              <span v-if="item.materializer"> · materializer: {{ shortMaterializer(item.materializer) }}</span>
            </div>
          </div>
          <font-awesome-icon :icon="['fas', 'chevron-right']" class="text-[10px] text-gray-300" />
        </div>
        <div v-if="!items.length" class="text-xs text-gray-400 text-center py-6">No analyses defined</div>
      </div>
    </div>

    <!-- Editor View -->
    <div v-else>
      <div class="flex items-center gap-2 mb-4">
        <button class="text-xs text-gray-400 hover:text-gray-700" @click="cancel">&larr; Back</button>
      </div>

      <!-- Header -->
      <div class="flex items-start gap-4 pb-4 border-b border-gray-200 mb-5">
        <div class="flex-1 min-w-0">
          <input
            v-model="editing.name"
            type="text"
            class="w-full text-sm font-semibold text-gray-800 px-3 py-1.5 border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-dark mb-1.5"
            placeholder="Analysis name…"
            @input="dirty = true"
          />
          <textarea
            v-model="editing.description"
            rows="2"
            class="w-full text-sm text-gray-800 px-3 py-1.5 border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-dark resize-none"
            placeholder="Short description…"
            @input="dirty = true"
          />
        </div>
        <button
          @click="editing.active = !editing.active; dirty = true"
          class="inline-flex items-center gap-1.5 text-[11px] px-2.5 py-1 rounded border transition-colors"
          :class="editing.active ? 'text-green-700 border-green-200 bg-green-50' : 'text-gray-500 border-gray-200 bg-white'"
        >
          <span class="w-1.5 h-1.5 rounded-full" :class="editing.active ? 'bg-green-500' : 'bg-gray-300'" />
          {{ editing.active ? 'Active' : 'Inactive' }}
        </button>
      </div>

      <!-- 1. SOURCES -->
      <section class="mb-6">
        <div class="flex items-baseline gap-2 mb-3 pb-1.5 border-b border-gray-100">
          <h3 class="text-[10px] font-semibold uppercase tracking-wider text-gray-400 flex-1">1. Sources</h3>
          <div class="relative" ref="sourcePickerRef">
            <button class="text-[11px] text-blue-600 hover:text-blue-800 font-medium" @click="toggleSourcePicker">+ Add Source</button>
            <div v-if="sourcePickerOpen" class="absolute right-0 top-6 z-10 w-80 bg-white border border-gray-200 rounded shadow-lg max-h-72 overflow-y-auto">
              <button
                class="w-full text-left px-3 py-2 text-xs text-blue-600 hover:bg-blue-50 border-b border-gray-200 font-medium"
                @click="openNewSourceFromPicker"
              >+ Create new Source Config…</button>
              <div v-if="!availableSourcesToAdd.length" class="px-3 py-3 text-xs text-gray-400 text-center">All source configs already added</div>
              <button
                v-for="src in availableSourcesToAdd"
                :key="src.id"
                class="w-full text-left px-3 py-1.5 hover:bg-gray-50 flex items-center gap-2"
                @click="addSourceToAnalysis(src)"
              >
                <span class="text-xs text-gray-800 truncate flex-1">{{ src.name }}</span>
                <span class="text-[10px] px-1 py-0.5 rounded bg-blue-50 text-blue-700 font-mono">{{ src.source_class }}</span>
              </button>
            </div>
          </div>
        </div>
        <div v-if="!selectedSources.length" class="text-[11px] text-gray-400 italic py-2">
          No sources added. Click "+ Add Source".
        </div>
        <div v-else class="space-y-1.5">
          <div
            v-for="src in selectedSources"
            :key="src.id"
            @click="openSourceModal(src)"
            class="px-3 py-2 border border-gray-200 rounded hover:bg-gray-50 cursor-pointer flex items-start gap-2"
          >
            <div class="flex-1 min-w-0">
              <div class="text-xs font-medium text-gray-800 flex items-center gap-1.5">
                {{ src.name }}
                <span class="text-[10px] px-1 py-0.5 rounded bg-blue-50 text-blue-700 font-mono">{{ src.source_class }}</span>
                <span
                  v-if="src.delivery_mode === 'queryable_table'"
                  class="text-[10px] px-1 py-0.5 rounded bg-orange-50 text-orange-700 font-mono"
                >queryable</span>
                <span v-if="!src.required" class="text-[10px] px-1 py-0.5 rounded bg-amber-50 text-amber-700">optional</span>
              </div>
              <div v-if="src.description" class="text-[10px] text-gray-500 mt-0.5">{{ src.description }}</div>
            </div>
            <button class="text-[10px] text-gray-400 hover:text-red-500 px-1 shrink-0" @click.stop="removeSource(src.id)">×</button>
          </div>
        </div>
      </section>

      <!-- 2. TASKS -->
      <section class="mb-6">
        <div class="flex items-baseline gap-2 mb-3 pb-1.5 border-b border-gray-100">
          <h3 class="text-[10px] font-semibold uppercase tracking-wider text-gray-400 flex-1">2. Tasks (ordered)</h3>
          <div class="relative" ref="taskPickerRef">
            <button class="text-[11px] text-blue-600 hover:text-blue-800 font-medium" @click="toggleTaskPicker">+ Add Task</button>
            <div v-if="taskPickerOpen" class="absolute right-0 top-6 z-10 w-72 bg-white border border-gray-200 rounded shadow-lg max-h-72 overflow-y-auto">
              <button
                class="w-full text-left px-3 py-2 text-xs text-blue-600 hover:bg-blue-50 border-b border-gray-200 font-medium"
                @click="openNewTaskFromPicker"
              >+ Create new Task…</button>
              <div v-if="!availableTasksToAdd.length" class="px-3 py-3 text-xs text-gray-400 text-center">All tasks already wired</div>
              <button
                v-for="t in availableTasksToAdd"
                :key="t.id"
                class="w-full text-left px-3 py-1.5 hover:bg-gray-50 flex items-center gap-2"
                @click="addTaskToAnalysis(t)"
              >
                <span class="text-xs text-gray-800 truncate flex-1">{{ t.name }}</span>
                <span class="text-[10px] px-1 py-0.5 rounded bg-purple-50 text-purple-700 font-mono">v{{ t.version }}</span>
              </button>
            </div>
          </div>
        </div>

        <div v-if="!editing.id" class="text-[11px] text-amber-600 italic py-2">
          Save the Analysis first, then add Tasks.
        </div>
        <div v-else-if="!editing.task_links || !editing.task_links.length" class="text-[11px] text-gray-400 italic py-2">
          No tasks wired.
        </div>
        <div v-else class="space-y-1.5">
          <div
            v-for="(link, idx) in editing.task_links"
            :key="link.id"
            @click="openTaskModal(link)"
            class="px-3 py-2 border border-gray-200 rounded flex items-center gap-2 hover:bg-gray-50 cursor-pointer"
          >
            <span class="text-[10px] font-mono text-gray-400 w-6 shrink-0">#{{ idx + 1 }}</span>
            <span class="text-xs font-medium text-gray-800">{{ link.task_name }}</span>
            <span class="text-[10px] px-1 py-0.5 rounded bg-purple-50 text-purple-700 font-mono">v{{ link.task_version }}</span>
            <span
              v-if="latestVersionFor(link.task_name) > link.task_version"
              class="text-[10px] px-1 py-0.5 rounded bg-amber-50 text-amber-700 cursor-pointer"
              @click.stop="upgradeTask(link)"
              :title="`Upgrade to v${latestVersionFor(link.task_name)}`"
            >v{{ link.task_version }} → v{{ latestVersionFor(link.task_name) }} — Upgrade</span>
            <div class="flex-1" />
            <button class="text-[10px] text-gray-400 hover:text-gray-700 px-1" :disabled="idx === 0" @click.stop="moveTask(idx, -1)">▲</button>
            <button class="text-[10px] text-gray-400 hover:text-gray-700 px-1" :disabled="idx === editing.task_links.length - 1" @click.stop="moveTask(idx, 1)">▼</button>
            <button class="text-[10px] text-gray-400 hover:text-red-500 px-1" @click.stop="removeTaskFromAnalysis(idx)">×</button>
          </div>
        </div>
        <p v-if="taskOpError" class="text-[10px] text-red-500 mt-1">{{ taskOpError }}</p>
      </section>

      <!-- 3. META INSTRUCTION -->
      <section class="mb-6">
        <h3 class="text-[10px] font-semibold uppercase tracking-wider text-gray-400 mb-3 pb-1.5 border-b border-gray-100">3. Meta Instruction</h3>
        <textarea
          v-model="editing.meta_instruction"
          rows="4"
          class="w-full text-xs px-2 py-1.5 border border-gray-200 rounded bg-white focus:outline-none focus:border-gray-400 resize-vertical leading-relaxed"
          placeholder="Optional — ties multiple Tasks together. Leave empty for single-Task Analyses."
          @input="dirty = true"
        />
      </section>

      <!-- 4. MODEL & EXECUTION -->
      <section class="mb-6">
        <h3 class="text-[10px] font-semibold uppercase tracking-wider text-gray-400 mb-3 pb-1.5 border-b border-gray-100">4. Model & Execution</h3>
        <div class="grid grid-cols-3 gap-3 mb-3">
          <div>
            <label class="block text-[11px] font-medium text-gray-600 mb-1">Model</label>
            <select
              v-model="editing.model"
              @change="dirty = true"
              class="w-full text-xs px-2 py-1.5 border border-gray-200 rounded bg-white text-gray-800 focus:outline-none focus:border-gray-400"
            >
              <option value="">—</option>
              <option v-for="m in MODEL_OPTIONS" :key="m.id" :value="m.id">{{ m.label }}</option>
            </select>
          </div>
          <div>
            <label class="block text-[11px] font-medium text-gray-600 mb-1">Max tokens</label>
            <input
              v-model.number="editing.max_tokens"
              type="number"
              class="w-full text-xs px-2 py-1.5 border border-gray-200 rounded bg-white focus:outline-none focus:border-gray-400"
              @input="dirty = true"
            />
          </div>
          <div>
            <label class="block text-[11px] font-medium text-gray-600 mb-1">Temperature</label>
            <input
              v-model.number="editing.temperature"
              type="number"
              step="0.1"
              min="0"
              max="2"
              class="w-full text-xs px-2 py-1.5 border border-gray-200 rounded bg-white focus:outline-none focus:border-gray-400"
              @input="dirty = true"
            />
          </div>
        </div>

        <div class="grid grid-cols-3 gap-3">
          <div>
            <label class="block text-[11px] font-medium text-gray-600 mb-1">Execution mode</label>
            <select
              v-model="editing.execution_mode"
              @change="onExecutionModeChange"
              class="w-full text-xs px-2 py-1.5 border border-gray-200 rounded bg-white text-gray-800 focus:outline-none focus:border-gray-400"
            >
              <option value="one_shot">One-shot</option>
              <option value="agentic">Agentic</option>
            </select>
          </div>
          <div>
            <label class="block text-[11px] font-medium text-gray-600 mb-1">
              Max turns
              <span v-if="editing.execution_mode === 'one_shot'" class="text-gray-400">(locked at 1)</span>
            </label>
            <input
              v-model.number="editing.max_turns"
              type="number"
              min="1"
              :disabled="editing.execution_mode === 'one_shot'"
              class="w-full text-xs px-2 py-1.5 border border-gray-200 rounded bg-white focus:outline-none focus:border-gray-400 disabled:bg-gray-50 disabled:text-gray-400"
              @input="dirty = true"
            />
          </div>
          <div>
            <label class="block text-[11px] font-medium text-gray-600 mb-1">
              Max rows per query
              <span v-if="editing.execution_mode === 'one_shot'" class="text-gray-400">(agentic only)</span>
            </label>
            <input
              v-model.number="editing.max_rows_per_query"
              type="number"
              min="1"
              :disabled="editing.execution_mode === 'one_shot'"
              class="w-full text-xs px-2 py-1.5 border border-gray-200 rounded bg-white focus:outline-none focus:border-gray-400 disabled:bg-gray-50 disabled:text-gray-400"
              @input="dirty = true"
            />
          </div>
        </div>

        <p class="text-[10px] text-gray-400 mt-2">
          <span v-if="editing.execution_mode === 'one_shot'">
            Single LLM call. All Sources must be <code>inline_data</code>.
          </span>
          <span v-else>
            Multi-turn with <code>run_query</code> + <code>save_finding</code> tools. Sources can mix <code>inline_data</code> and <code>queryable_table</code>.
          </span>
        </p>

        <p v-if="executionModeMismatch" class="text-[10px] text-red-500 mt-1">{{ executionModeMismatch }}</p>
      </section>

      <!-- 5. MATERIALIZER (read-only) -->
      <section class="mb-6">
        <h3 class="text-[10px] font-semibold uppercase tracking-wider text-gray-400 mb-3 pb-1.5 border-b border-gray-100">5. Materializer</h3>
        <div class="text-xs text-gray-600 font-mono px-2 py-1.5 bg-gray-50 border border-gray-200 rounded">
          {{ editing.materializer || '— (set in code via IntelligenceAnalysis.materializer)' }}
        </div>
      </section>

      <!-- FOOTER -->
      <div class="flex items-center gap-2.5 pt-4 border-t border-gray-200">
        <span class="text-[11px] mr-auto" :class="dirty ? 'text-blue-600' : 'text-gray-400'">
          {{ dirty ? 'Unsaved changes' : 'Up to date' }}
        </span>
        <button
          disabled
          class="text-xs px-3 py-1.5 rounded border border-gray-200 text-gray-400 bg-white cursor-not-allowed"
          title="Composer not yet built — lands after this UI is wired"
        >Test Run</button>
        <button @click="cancel" :disabled="!dirty" class="text-xs px-3 py-1.5 rounded border border-gray-200 text-gray-600 bg-white hover:bg-gray-50 disabled:opacity-40 disabled:cursor-not-allowed">Discard</button>
        <button @click="save" :disabled="!dirty || saving" class="text-xs px-3 py-1.5 rounded bg-gray-900 text-white hover:bg-gray-800 disabled:opacity-40 disabled:cursor-not-allowed">
          {{ saving ? 'Saving…' : (editing.id ? 'Save' : 'Create') }}
        </button>
        <span v-if="saveError" class="text-[10px] text-red-500">{{ saveError }}</span>
      </div>
    </div>

    <!-- Modals -->
    <SourceConfigModal
      v-if="showSourceModal"
      :initial="sourceModalInitial"
      :source-classes="registrySourceClasses"
      @close="showSourceModal = false"
      @saved="onSourceSaved"
    />
    <TaskModal
      v-if="showTaskModal"
      :initial="taskModalInitial"
      @close="showTaskModal = false"
      @saved="onTaskSaved"
    />
  </div>
</template>

<script>
import axios from 'axios'
import LoadingIndicator from '@/components/widgetcomponents/library/LoadingIndicator.vue'
import SourceConfigModal from './SourceConfigModal.vue'
import TaskModal from './TaskModal.vue'

const API_BASE = 'api/intelligence/'

// Keep in sync with MODEL_REGISTRY in api/intelligence/query.py.
const MODEL_OPTIONS = [
  { id: 'gpt-5.5',           label: 'GPT-5.5 (OpenAI)' },
  { id: 'claude-opus-4-8',   label: 'Claude Opus 4.8 (Anthropic)' },
  { id: 'claude-sonnet-4-6', label: 'Claude Sonnet 4.6 (Anthropic)' },
]

export default {
  name: 'AnalysisEditor',
  components: { LoadingIndicator, SourceConfigModal, TaskModal },

  data() {
    return {
      MODEL_OPTIONS,
      items: [],
      availableSources: [],
      registrySourceClasses: [],
      allTasks: [],
      loading: true,
      editing: null,
      dirty: false,
      saving: false,
      saveError: null,
      showSourceModal: false,
      showTaskModal: false,
      sourceModalInitial: {},
      taskModalInitial: {},
      taskPickerOpen: false,
      sourcePickerOpen: false,
      taskOpError: null,
    }
  },

  computed: {
    selectedSourceIds() {
      return (this.editing?.sources || []).map(s => (typeof s === 'object' ? s.id : s))
    },

    selectedSources() {
      const byId = new Map(this.availableSources.map(s => [s.id, s]))
      return this.selectedSourceIds.map(id => byId.get(id)).filter(Boolean)
    },

    availableSourcesToAdd() {
      const selected = new Set(this.selectedSourceIds)
      return this.availableSources.filter(s => !selected.has(s.id))
    },

    latestVersionByName() {
      const map = {}
      for (const t of this.allTasks) {
        if (!map[t.name] || t.version > map[t.name]) map[t.name] = t.version
      }
      return map
    },

    latestPerName() {
      const map = {}
      for (const t of this.allTasks) {
        if (!map[t.name] || t.version > map[t.name].version) map[t.name] = t
      }
      return map
    },

    availableTasksToAdd() {
      if (!this.editing) return []
      const wiredNames = new Set((this.editing.task_links || []).map(l => l.task_name))
      return Object.values(this.latestPerName)
        .filter(t => !wiredNames.has(t.name) && t.active)
        .sort((a, b) => a.name.localeCompare(b.name))
    },

    executionModeMismatch() {
      if (!this.editing || this.editing.execution_mode !== 'one_shot') return null
      const ids = this.selectedSourceIds
      const queryable = this.availableSources
        .filter(s => ids.includes(s.id) && s.delivery_mode === 'queryable_table')
      if (!queryable.length) return null
      return (
        `one_shot can't include queryable_table Sources: ${queryable.map(s => s.name).join(', ')}. ` +
        `Switch to Agentic, or change them to inline_data.`
      )
    },
  },

  methods: {
    async fetchData() {
      this.loading = true
      try {
        const [analysesRes, sourcesRes, registryRes, tasksRes] = await Promise.all([
          axios.get(`${API_BASE}analyses/`),
          axios.get(`${API_BASE}source-configs/`),
          axios.get(`${API_BASE}registry/`),
          axios.get(`${API_BASE}tasks/`),
        ])
        this.items = analysesRes.data.results || analysesRes.data
        this.availableSources = sourcesRes.data.results || sourcesRes.data
        this.registrySourceClasses = registryRes.data.source_classes || []
        this.allTasks = tasksRes.data.results || tasksRes.data
      } catch (e) {
        console.error('Error loading analyses:', e)
      } finally {
        this.loading = false
      }
    },

    createNew() {
      this.editing = {
        name: '',
        description: '',
        active: true,
        sources: [],
        task_links: [],
        meta_instruction: '',
        model: '',
        max_tokens: 4000,
        temperature: 0.2,
        execution_mode: 'one_shot',
        max_turns: 1,
        max_rows_per_query: 500,
        materializer: '',
      }
      this.dirty = false
      this.saveError = null
    },

    onExecutionModeChange() {
      // Force max_turns back to 1 when switching to one_shot; bump default
      // to 120 when switching to agentic if user hasn't customized it.
      if (this.editing.execution_mode === 'one_shot') {
        this.editing.max_turns = 1
      } else if (!this.editing.max_turns || this.editing.max_turns === 1) {
        this.editing.max_turns = 120
      }
      this.dirty = true
    },

    editItem(item) {
      this.editing = JSON.parse(JSON.stringify(item))
      this.dirty = false
      this.saveError = null
    },

    cancel() {
      this.editing = null
      this.dirty = false
      this.saveError = null
    },

    toggleSourcePicker() {
      this.sourcePickerOpen = !this.sourcePickerOpen
    },

    addSourceToAnalysis(src) {
      this.sourcePickerOpen = false
      if (!this.selectedSourceIds.includes(src.id)) {
        this.editing.sources = [...this.selectedSourceIds, src.id]
        this.dirty = true
      }
    },

    removeSource(id) {
      this.editing.sources = this.selectedSourceIds.filter(x => x !== id)
      this.dirty = true
    },

    openSourceModal(src) {
      this.sourceModalInitial = src
      this.showSourceModal = true
    },

    shortMaterializer(path) {
      return path.split('.').pop()
    },

    latestVersionFor(taskName) {
      return this.latestVersionByName[taskName] || 0
    },

    openNewSourceFromPicker() {
      this.sourcePickerOpen = false
      this.sourceModalInitial = {}
      this.showSourceModal = true
    },

    async onSourceSaved(saved) {
      this.showSourceModal = false
      this.sourceModalInitial = {}
      await this.fetchData()
      // Auto-add a newly created Source to the current Analysis (edits of an
      // existing Source are already in the list, so this is a no-op for them).
      if (this.editing && saved?.id && !this.selectedSourceIds.includes(saved.id)) {
        this.editing.sources = [...this.selectedSourceIds, saved.id]
        this.dirty = true
      }
    },

    toggleTaskPicker() {
      if (!this.editing.id) {
        this.taskOpError = 'Save the Analysis first.'
        return
      }
      this.taskOpError = null
      this.taskPickerOpen = !this.taskPickerOpen
    },

    openNewTaskFromPicker() {
      this.taskPickerOpen = false
      this.taskModalInitial = {}
      this.showTaskModal = true
    },

    openTaskModal(link) {
      const full = this.allTasks.find(t => t.id === link.task)
      this.taskModalInitial = full || { id: link.task, name: link.task_name, version: link.task_version }
      this.showTaskModal = true
    },

    async onTaskSaved(saved) {
      this.showTaskModal = false
      this.taskModalInitial = {}
      await this.fetchData()
      if (!this.editing?.id || !saved?.id) return
      // Saving a Task always creates a new version. If this Analysis already
      // wires that Task name, re-point the link to the new version; otherwise
      // wire the new Task in.
      const links = this.editing.task_links || []
      const matchIdx = links.findIndex(l => l.task_name === saved.name)
      let payload
      if (matchIdx >= 0) {
        payload = links.map((l, i) => ({ task: i === matchIdx ? saved.id : l.task, order: i }))
      } else {
        payload = [
          ...links.map((l, i) => ({ task: l.task, order: i })),
          { task: saved.id, order: links.length },
        ]
      }
      await this.persistTaskLinks(payload)
    },

    async addTaskToAnalysis(task) {
      this.taskPickerOpen = false
      this.taskOpError = null
      const newLinks = [
        ...(this.editing.task_links || []),
        { task: task.id, order: (this.editing.task_links || []).length },
      ]
      await this.persistTaskLinks(newLinks)
    },

    async removeTaskFromAnalysis(idx) {
      const remaining = (this.editing.task_links || []).filter((_, i) => i !== idx)
      const reordered = remaining.map((l, i) => ({ task: l.task, order: i }))
      await this.persistTaskLinks(reordered)
    },

    async moveTask(idx, dir) {
      const links = [...(this.editing.task_links || [])]
      const target = idx + dir
      if (target < 0 || target >= links.length) return
      ;[links[idx], links[target]] = [links[target], links[idx]]
      const reordered = links.map((l, i) => ({ task: l.task, order: i }))
      await this.persistTaskLinks(reordered)
    },

    async persistTaskLinks(taskLinksPayload) {
      this.taskOpError = null
      try {
        const { data } = await axios.post(
          `${API_BASE}analyses/${this.editing.id}/set-tasks/`,
          { task_links: taskLinksPayload },
        )
        this.editing.task_links = data.task_links || []
        this.syncListItem()
      } catch (e) {
        this.taskOpError = e.response?.data?.error
          || e.response?.data?.detail
          || JSON.stringify(e.response?.data || {})
          || 'Failed to update tasks'
      }
    },

    async upgradeTask(link) {
      this.taskOpError = null
      try {
        const { data } = await axios.post(
          `${API_BASE}analyses/${this.editing.id}/upgrade-task/`,
          { task: link.task },
        )
        // Refresh the analysis to pick up the updated task_links shape.
        const { data: refreshed } = await axios.get(`${API_BASE}analyses/${this.editing.id}/`)
        this.editing.task_links = refreshed.task_links || []
        this.syncListItem()
      } catch (e) {
        this.taskOpError = e.response?.data?.error
          || e.response?.data?.detail
          || JSON.stringify(e.response?.data || {})
          || 'Upgrade failed'
      }
    },

    async save() {
      this.saveError = null
      if (!this.editing.name) { this.saveError = 'Name required'; return }
      if (!this.editing.model) { this.saveError = 'Model required'; return }
      if (this.executionModeMismatch) { this.saveError = this.executionModeMismatch; return }

      this.saving = true
      try {
        const payload = {
          name: this.editing.name,
          description: this.editing.description || '',
          active: this.editing.active,
          sources: this.selectedSourceIds,
          meta_instruction: this.editing.meta_instruction || '',
          model: this.editing.model,
          max_tokens: this.editing.max_tokens,
          temperature: this.editing.temperature,
          execution_mode: this.editing.execution_mode || 'one_shot',
          max_turns: this.editing.max_turns ?? 1,
          max_rows_per_query: this.editing.max_rows_per_query ?? 500,
        }
        if (this.editing.id) {
          const { data } = await axios.patch(`${API_BASE}analyses/${this.editing.id}/`, payload)
          this.editing = { ...this.editing, ...data }
        } else {
          const { data } = await axios.post(`${API_BASE}analyses/`, payload)
          this.editing = { ...this.editing, ...data }
        }
        this.dirty = false
        this.fetchData()
      } catch (e) {
        this.saveError = e.response?.data?.detail
          || JSON.stringify(e.response?.data || {})
          || 'Save failed'
      } finally {
        this.saving = false
      }
    },

    syncListItem() {
      // Keep the left-hand list's task-count in sync after immediate task
      // ops (set-tasks / upgrade) so it isn't stale until a full reload.
      if (!this.editing?.id) return
      const item = this.items.find(i => i.id === this.editing.id)
      if (item) item.task_links = this.editing.task_links
    },

    handleClickOutside(e) {
      if (this.taskPickerOpen && this.$refs.taskPickerRef && !this.$refs.taskPickerRef.contains(e.target)) {
        this.taskPickerOpen = false
      }
      if (this.sourcePickerOpen && this.$refs.sourcePickerRef && !this.$refs.sourcePickerRef.contains(e.target)) {
        this.sourcePickerOpen = false
      }
    },
  },

  mounted() {
    this.fetchData()
    document.addEventListener('click', this.handleClickOutside)
  },

  beforeUnmount() {
    document.removeEventListener('click', this.handleClickOutside)
  },
}
</script>
